import { useEffect } from "react";
import { useLocation, useSearchParams, Link, useLoaderData, Outlet, useFetcher } from "react-router-dom";
//--------------------------------------------------------------------------------------------
import ServerFooter from "../common/ServerFooter";
import { Lead } from "./types";
export const extraFields = ["page", "limit"];
type MailAddress = {
    email: string;
    name: string;
    full: string;
};
type SentEmail = {
    date: string;
    subject: string;
    to: MailAddress[];
    cc: MailAddress[];
    bcc: MailAddress[];
};
type SentEmailResponse = {
    limit: number;
    total: number;
    emails: SentEmail[];
};
//--------------------------------------------------------------------------------------------
export default function LeadList(){
    const [searchParams, setSearchParams] = useSearchParams();
    const { leads, total }:{ leads: Lead[], total: number } = useLoaderData();
    const location = useLocation();
    const sendEmailFetcher = useFetcher<{ sent: number; skippedInvalidEmail: number; failed: number; halted?: boolean; haltReason?: string } | null>();
    const sentEmailsFetcher = useFetcher<SentEmailResponse | null>();
    const onFilter = [...searchParams.keys()].filter(param => !extraFields.includes(param)).length > 0;
    const noOrganizationAssociated = searchParams.get("noOrganizationAssociated") === "1";
    const sendingEmail = sendEmailFetcher.state !== "idle";
    const loadingSentEmails = sentEmailsFetcher.state !== "idle";
    useEffect(() => {
        if(!sendEmailFetcher.data) return;
        const { sent, skippedInvalidEmail, failed, halted, haltReason } = sendEmailFetcher.data;
        window.alert(`Sent ${sent} lead email${sent === 1 ? "" : "s"}.${skippedInvalidEmail ? ` Skipped ${skippedInvalidEmail} invalid email${skippedInvalidEmail === 1 ? "" : "s"}.` : ""}${failed ? ` Failed: ${failed}.` : ""}${halted ? ` Sending stopped: ${haltReason || "SMTP rejected the batch."}` : ""}`);
    }, [sendEmailFetcher.data]);
    const handleSendEmail = () => {
        if(!window.confirm("Send email to all unsent leads matching the current filter?")) return;
        sendEmailFetcher.submit(new FormData(), {
            method: "post",
            action: `SendEmail${location.search}`,
        });
    };
    const handleLoadSentEmails = () => {
        const formData = new FormData();
        formData.append("limit", "5");
        sentEmailsFetcher.submit(formData, {
            method: "post",
            action: "SentEmails",
        });
    };
    const renderAddresses = (addresses: MailAddress[]) => {
        if(!addresses.length) return "";
        return addresses.map(address => address.name ? `${address.name} <${address.email || address.full}>` : address.email || address.full).join(", ");
    };
    //--------------------------------------------------------------------------------------------
    return(
        <div className = {"cssContent" + (total > 0?" pb8rem":"")}>
            <div className = "mt-2">
                <Link className = {"btn btn-outline-primary pointer position-relative"} to = {{ pathname: "Filter", search: location.search }} replace>
                    Filter
                {onFilter?<span children = "on" className = "badge text-bg-primary position-absolute top-0 start-100 translate-middle" style = {{minWidth: "2.4em"}}/>:null}
                </Link>
                <Link children = "Export" className = "btn btn-outline-primary pointer ms-3" to = {{ pathname: "Export", search: location.search }}/>
                <button
                    children = {sendingEmail ? "Sending..." : "Send Email"}
                    className = "btn btn-outline-primary pointer ms-3"
                    disabled = {sendingEmail}
                    onClick = {handleSendEmail}
                    type = "button"
                />
                <button
                    children = {loadingSentEmails ? "Loading Sent..." : "Sent Emails"}
                    className = "btn btn-outline-primary pointer ms-3"
                    disabled = {loadingSentEmails}
                    onClick = {handleLoadSentEmails}
                    type = "button"
                />
                <input
                    type = "checkbox"
                    checked = {noOrganizationAssociated}
                    onChange = {e => {
                        const checked = e.currentTarget.checked;
                        const params = new URLSearchParams(searchParams);
                        if(checked) params.set("noOrganizationAssociated", "1");
                        else params.delete("noOrganizationAssociated");
                        setSearchParams(params, { replace: false });
                    }}
                    className = "cssCheckBox ms-3"
                />
                <label children = "No Organization Associated" htmlFor = "noOrg" className = "ms-2"/>
                <Link children = "Add" className = "btn btn-outline-primary float-end" to = "Add"/>
            </div>
            {sentEmailsFetcher.data ?
            <div className = "border rounded p-3 mt-3">
                <div className = "d-flex align-items-center mb-2">
                    <h5 className = "mb-0">Sent Emails</h5>
                    <span className = "text-muted ms-3">
                        Latest {sentEmailsFetcher.data.total} sent emails loaded
                    </span>
                </div>
                {!sentEmailsFetcher.data.emails.length ?
                <div className = "cssLoading">No sent emails found</div> :
                <div className = "table-responsive">
                    <table className = "table table-sm">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>To</th>
                                <th>CC</th>
                                <th>BCC</th>
                                <th>Subject</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sentEmailsFetcher.data.emails.map((email, key) =>
                            <tr key = {`${email.date}-${key}`}>
                                <td>{email.date}</td>
                                <td>{renderAddresses(email.to)}</td>
                                <td>{renderAddresses(email.cc)}</td>
                                <td>{renderAddresses(email.bcc)}</td>
                                <td>{email.subject}</td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>}
            </div> : null}
            {!leads.length?<div className = "cssLoading">Empty</div>:
            <div className = "table-responsive">
                <table className = "table">
                    <thead className = "noTableTopBorder">
                        <tr>
                            <th>Lead Name</th>
                            <th>Role</th>
                            <th>Organization Name</th>
                            <th>Business Phone</th>
                            <th>Email</th>
                            <th>Sent</th>
                            <th>Real Sent</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {leads.map((lead, key) => 
                        <tr key = {key}>
                            <td>{lead.name}</td>
                            <td>{lead.role}</td>
                            <td>{lead.organizationName && <Link to = {`/Organizations/${lead.organizationId}`}>{lead.organizationName}</Link>}</td>
                            <td>
                                {lead.businessPhone && 
                                <a href = {`tel:+1${lead.businessPhone.replace(/-/g, "")}${lead.extension?`,${lead.extension}`:""}`}>
                                    {lead.businessPhone}{lead.extension?` Ext. ${lead.extension}`:null}
                                </a>}
                            </td>
                            <td>{lead.email && <a href = {`mailto:${lead.email}`} children = {lead.email}/>}</td>
                            <td>{Number(lead.sent) === 1 ? "Yes" : "No"}</td>
                            <td>{Number(lead.realSent) === 1 ? "Yes" : "No"}</td>
                            <td><Link children = "Detail" className = "btn btn-outline-primary" to = {String(lead.id)}/></td>
                        </tr>)}
                    </tbody>
                </table>
            </div>}
            {total > 0? <ServerFooter length = {total}/> : null}
            <Outlet/>
        </div>
    )
}
